import {useEffect, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Skeleton} from '@/components/ui/skeleton'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DateField} from '@/components/date-field'
import {EmployeePicker} from '@/components/employee-picker'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {SegmentedControl} from '@/components/segmented-control'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {useProveedores, type Proveedor} from '@/hooks/empresa/proveedores/use-proveedores'
import {
  ASIGNACIONES_PAGE_SIZE,
  useAsignaciones,
  useAsignar,
  useDeclararLabor,
  useDesasignar,
  useEscribirVinculos,
  usePedirLabor,
  type Asignacion,
  type LaborCamino,
  type Obra,
} from '@/hooks/empresa/obras/use-obras'
import {marcarErrorComoComunicado, translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

const CAMINOS: LaborCamino[] = ['heredada', 'escrita', 'declarada_contratista']

/** Desasignar no borra, y «sin labor declarada» se dice de la asignación, nunca de la persona (§7). */
export function ObraGente({
  businessId,
  proyectoId,
  obra,
  canEdit,
}: {
  businessId: string
  proyectoId: string
  obra: Obra
  canEdit: boolean
}) {
  const {t} = useTranslation()
  // Una obra terminada no tiene gente vigente (§7): abre en la historia.
  const [historia, setHistoria] = useState(obra.estado === 'terminado')
  const [page, setPage] = useState(1)
  const [asignando, setAsignando] = useState(false)
  const [vinculando, setVinculando] = useState(false)
  const [laborTarget, setLaborTarget] = useState<Asignacion | null>(null)
  const [salidaTarget, setSalidaTarget] = useState<Asignacion | null>(null)
  const [errorFila, setErrorFila] = useState<string | null>(null)
  const {data, isLoading} = useAsignaciones(businessId, proyectoId, historia, page)
  const desasignar = useDesasignar(businessId, proyectoId)
  const pedirLabor = usePedirLabor(businessId, proyectoId)

  function verHistoria(clave: string) {
    setHistoria(clave === 'historia')
    setPage(1)
  }

  if (isLoading || !data) return <Skeleton className="h-48 w-full" />

  return (
    <div className="space-y-4" data-testid="obra-gente">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heading text-base text-foreground">{t('obra.gente.title')}</p>
          {data.total > 0 && (
            <p
              className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase"
              data-testid="obra-gente-conteo">
              {t('obra.gente.conteo', {visibles: data.items.length, total: data.total})}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SegmentedControl
            value={historia ? 'historia' : 'vigentes'}
            options={[
              {key: 'vigentes', label: t('obra.gente.verVigentes')},
              {key: 'historia', label: t('obra.gente.verHistoria')},
            ]}
            size="sm"
            testId="obra-gente-historia"
            onChange={verHistoria}
          />
          {canEdit && (
            <>
              <Button size="sm" variant="outline" onClick={() => setVinculando(true)} data-testid="obra-vinculos">
                {t('obra.gente.empresaContratada')}
              </Button>
              <Button size="sm" onClick={() => setAsignando(true)} data-testid="obra-asignar">
                {t('obra.gente.asignar')}
              </Button>
            </>
          )}
        </div>
      </div>

      {data.items.length === 0 ? (
        <div className="surface px-6 py-12 text-center" data-testid="obra-gente-vacio">
          <p className="font-heading text-lg text-foreground">
            {historia ? t('obra.gente.vacioHistoria') : t('obra.gente.vacio')}
          </p>
          {!historia && <p className="mt-1 text-sm text-muted-foreground">{t('obra.gente.vacioAccion')}</p>}
        </div>
      ) : (
        <ul className="space-y-2">
          {data.items.map((asignacion) => (
            <li
              key={asignacion.id}
              className="surface flex items-start gap-3 px-4 py-3"
              data-testid={`asignacion-${asignacion.id}`}>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="font-medium text-foreground">{asignacion.nombre}</p>
                <p className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase">
                  {asignacion.hasta
                    ? t('obra.gente.yaNoAsignado', {fecha: asignacion.hasta})
                    : t('obra.gente.desdeEl', {fecha: asignacion.desde})}
                </p>
                <LaborLinea asignacion={asignacion} />
                {asignacion.contratista_empresa ? (
                  <p className="text-xs text-muted-foreground">
                    {t('obra.gente.contratista', {empresa: asignacion.contratista_empresa})}
                    {asignacion.contratista_escrito_por &&
                      ` — ${t('obra.vinculos.escritoPor', {
                        nombre: asignacion.contratista_escrito_por,
                        fecha: (asignacion.contratista_escrito_en ?? '').slice(0, 10),
                      })}`}
                  </p>
                ) : (
                  asignacion.es_contratista && (
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => setVinculando(true)}
                      data-testid={`asignacion-sin-empresa-${asignacion.id}`}>
                      {t('obra.gente.sinEmpresa')} · {t('obra.gente.sinEmpresaAccion')}
                    </button>
                  )
                )}
              </div>
              {canEdit && !asignacion.hasta && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid={`asignacion-menu-${asignacion.id}`}>
                      {t('obra.gente.acciones')}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setLaborTarget(asignacion)}>
                      {t('obra.gente.declararLabor')}
                    </DropdownMenuItem>
                    {asignacion.sin_labor && (
                      <DropdownMenuItem
                        onSelect={() => {
                          setErrorFila(null)
                          pedirLabor.mutate(asignacion.id, {
                            // Marcado como comunicado para que la red global no sume una segunda burbuja.
                            onError: (err) => {
                              marcarErrorComoComunicado(err)
                              setErrorFila(t('obra.gente.errorPedirLabor'))
                            },
                          })
                        }}>
                        {t('obra.gente.pedirLabor')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onSelect={() => setSalidaTarget(asignacion)}>
                      {t('obra.gente.desasignar')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </li>
          ))}
        </ul>
      )}

      {errorFila && (
        <p className="text-sm text-destructive" data-testid="obra-gente-error">
          {errorFila}
        </p>
      )}
      {data.total > 0 && (
        <PaginationBar page={page} pageSize={ASIGNACIONES_PAGE_SIZE} total={data.total} onPageChange={setPage} />
      )}

      <AsignarDialog businessId={businessId} proyectoId={proyectoId} open={asignando} onOpenChange={setAsignando} />
      <VinculosDialog businessId={businessId} proyectoId={proyectoId} open={vinculando} onOpenChange={setVinculando} />
      <LaborDialog
        businessId={businessId}
        proyectoId={proyectoId}
        asignacion={laborTarget}
        onClose={() => setLaborTarget(null)}
      />
      <ConfirmDialog
        open={!!salidaTarget}
        onOpenChange={(next) => !next && setSalidaTarget(null)}
        title={t('obra.gente.desasignarTitle')}
        description={t('obra.gente.desasignarBody')}
        porque={{
          label: t('obra.gente.desasignarPorqueLabel'),
          testId: 'obra-desasignar-porque',
          contenidoTestId: 'obra-desasignar-porque-contenido',
          contenido: <p>{t('obra.gente.desasignarPorqueBody')}</p>,
        }}
        confirmLabel={t('obra.gente.desasignar')}
        cancelLabel={t('common.cancel')}
        pending={desasignar.isPending}
        onConfirm={() => salidaTarget && desasignar.mutate(salidaTarget.id, {onSuccess: () => setSalidaTarget(null)})}
      />
    </div>
  )
}

function LaborLinea({asignacion}: {asignacion: Asignacion}) {
  const {t} = useTranslation()
  if (asignacion.sin_labor) {
    return (
      <div className="space-y-0.5" data-testid={`asignacion-sin-labor-${asignacion.id}`}>
        <Tag tone="amber">{t('obra.gente.sinLabor')}</Tag>
        <p className="text-xs text-muted-foreground">{t('obra.gente.sinLaborHint')}</p>
        {asignacion.labor_pedida_en && !asignacion.labor_respondida_en && (
          <p className="text-xs text-muted-foreground">
            {t('obra.gente.laborPedida', {fecha: asignacion.labor_pedida_en})}
          </p>
        )}
      </div>
    )
  }
  return (
    <div className="space-y-0.5">
      <p className="text-sm text-foreground">{asignacion.labor_texto}</p>
      <p className="text-xs text-muted-foreground" data-testid={`asignacion-labor-origen-${asignacion.id}`}>
        {asignacion.labor_camino === 'heredada'
          ? t('obra.labor.origen', {
              cargo: asignacion.labor_cargo ?? '',
              fecha: (asignacion.labor_version_confirmada_en ?? '').slice(0, 10),
            })
          : asignacion.labor_camino === 'declarada_contratista'
            ? t('obra.labor.origenContratista', {
                nombre: asignacion.labor_autor_nombre ?? '',
                fecha: (asignacion.labor_en ?? '').slice(0, 10),
              })
            : t('obra.labor.origenEscrita', {
                nombre: asignacion.labor_autor_nombre ?? '',
                fecha: (asignacion.labor_en ?? '').slice(0, 10),
              })}
      </p>
    </div>
  )
}

function AsignarDialog({
  businessId,
  proyectoId,
  open,
  onOpenChange,
}: {
  businessId: string
  proyectoId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const roster = useAllEmployees(open ? businessId : undefined)
  const asignar = useAsignar(businessId, proyectoId)
  const [seleccion, setSeleccion] = useState<Employee[]>([])
  const [desde, setDesde] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Sembrado en un efecto de `open`: Radix no reporta aperturas programáticas. `desde` arranca
  // vacío porque el hoy del negocio lo decide el backend, no el reloj del navegador (FE-02).
  useEffect(() => {
    if (!open) return
    setSeleccion([])
    setDesde('')
    setError(null)
  }, [open])

  function handleOpenChange(next: boolean) {
    onOpenChange(next)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!seleccion.length || validateRequired(desde)) return
    asignar.mutate(
      {employeeIds: seleccion.map((persona) => persona.id), desde},
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="obra-asignar-dialog">
        <DialogHeader>
          <DialogTitle>{t('obra.gente.asignarTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-xs text-muted-foreground">{t('obra.gente.asignarHint')}</p>
          <div className="space-y-2">
            <Label>{t('obra.gente.personas')}</Label>
            <EmployeePicker
              roster={roster.data ?? []}
              selected={seleccion}
              onChange={setSeleccion}
              lastSelection={[]}
            />
          </div>
          <DateField id="obra_desde" label={t('obra.gente.desde')} value={desde} onChange={setDesde} />
          <FormDialogFooter
            error={error}
            pending={asignar.isPending}
            submitDisabled={!seleccion.length || !desde}
            submitLabel={t('obra.gente.asignar')}
            onCancel={() => handleOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

function LaborDialog({
  businessId,
  proyectoId,
  asignacion,
  onClose,
}: {
  businessId: string
  proyectoId: string
  asignacion: Asignacion | null
  onClose: () => void
}) {
  const {t} = useTranslation()
  const declarar = useDeclararLabor(businessId, proyectoId)
  const [camino, setCamino] = useState<LaborCamino>('heredada')
  const [texto, setTexto] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Sembrado desde la asignación: arrancar en «heredada» sobrescribiría en silencio una labor escrita.
  useEffect(() => {
    if (!asignacion) return
    setCamino(asignacion.labor_camino ?? 'heredada')
    setTexto(asignacion.labor_camino && asignacion.labor_camino !== 'heredada' ? (asignacion.labor_texto ?? '') : '')
    setError(null)
  }, [asignacion])

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!asignacion) return
    declarar.mutate(
      {asignacionId: asignacion.id, camino, texto: camino === 'heredada' ? null : texto},
      {onSuccess: onClose, onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog
      open={!!asignacion}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}>
      <DialogContent data-testid="obra-labor-dialog">
        <DialogHeader>
          <DialogTitle>{t('obra.labor.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label>{t('obra.labor.camino')}</Label>
            <SegmentedControl
              value={camino}
              options={CAMINOS.map((clave) => ({key: clave, label: t(`obra.labor.${clave}`)}))}
              testId="labor-camino"
              onChange={setCamino}
            />
            <p className="text-xs text-muted-foreground">
              {camino === 'heredada'
                ? t('obra.labor.heredadaHint')
                : camino === 'declarada_contratista'
                  ? t('obra.labor.contratistaHint')
                  : ''}
            </p>
          </div>
          {camino !== 'heredada' && (
            <div className="space-y-2">
              <Label htmlFor="labor_texto">{t('obra.labor.texto')}</Label>
              <Textarea
                maxLength={tope('TEXTO')}
                id="labor_texto"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                data-testid="labor-texto"
              />
            </div>
          )}
          <FormDialogFooter
            error={error}
            pending={declarar.isPending}
            submitDisabled={camino !== 'heredada' && !texto.trim()}
            submitLabel={t('obra.labor.save')}
            onCancel={onClose}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Un vínculo por persona, cada uno con su constancia: Kairos no reparte por parecido. */
function VinculosDialog({
  businessId,
  proyectoId,
  open,
  onOpenChange,
}: {
  businessId: string
  proyectoId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const roster = useAllEmployees(open ? businessId : undefined)
  const [busqueda, setBusqueda] = useState('')
  const proveedores = useProveedores(open ? businessId : undefined, {
    page: 1,
    search: busqueda,
    sortBy: 'razon_social',
    sortDir: 'asc',
  })
  const escribir = useEscribirVinculos(businessId, proyectoId)
  const [seleccion, setSeleccion] = useState<Employee[]>([])
  const [proveedor, setProveedor] = useState<Proveedor | null>(null)
  const [empresaNombre, setEmpresaNombre] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setSeleccion([])
    setProveedor(null)
    setEmpresaNombre('')
    setBusqueda('')
    setError(null)
  }, [open])

  function handleOpenChange(next: boolean) {
    onOpenChange(next)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    escribir.mutate(
      {
        employeeIds: seleccion.map((persona) => persona.id),
        proveedorId: proveedor?.id ?? null,
        empresaNombre: proveedor ? null : empresaNombre.trim() || null,
      },
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="obra-vinculos-dialog">
        <DialogHeader>
          <DialogTitle>{t('obra.vinculos.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-xs text-muted-foreground">{t('obra.vinculos.hint')}</p>
          <div className="space-y-2">
            <Label>{t('obra.vinculos.personas')}</Label>
            <EmployeePicker
              roster={roster.data ?? []}
              selected={seleccion}
              onChange={setSeleccion}
              lastSelection={[]}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vinculo_proveedor">{t('obra.vinculos.proveedor')}</Label>
            {/* Búsqueda en el servidor: la primera página de proveedores deja fuera a casi todo el directorio. */}
            <EntitySearchSelect<Proveedor>
              id="vinculo_proveedor"
              items={proveedores.data?.items ?? []}
              value={proveedor}
              getKey={(fila) => fila.id}
              filterText={(fila) => fila.razon_social}
              renderOption={(fila) => fila.razon_social}
              renderTriggerValue={(fila) => fila.razon_social}
              placeholder={t('obra.vinculos.proveedor')}
              searchPlaceholder={t('obra.vinculos.proveedorPlaceholder')}
              noResultsLabel={t('obra.vinculos.sinProveedores')}
              clearLabel={t('common.cancel')}
              testId="vinculo-proveedor"
              onQueryChange={setBusqueda}
              onPick={setProveedor}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vinculo_empresa">{t('obra.vinculos.empresaNombre')}</Label>
            <Input
              maxLength={tope('NOTA')}
              id="vinculo_empresa"
              value={empresaNombre}
              disabled={!!proveedor}
              onChange={(e) => setEmpresaNombre(e.target.value)}
              data-testid="vinculo-empresa"
            />
          </div>
          <FormDialogFooter
            error={error}
            pending={escribir.isPending}
            submitDisabled={!seleccion.length || (!proveedor && !empresaNombre.trim())}
            submitLabel={t('obra.vinculos.escribir')}
            onCancel={() => handleOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
